// "Should You Submit Your Test Scores?" - student playbook. The test-optional
// question is one of the largest query families in admissions. The honest
// core: the submit/withhold line is per-school arithmetic against the
// admitted middle, not a single global decision.

const paper = {
    slug: 'should-you-submit-test-scores',
    type: 'whitepaper',
    title: 'Should You Submit Your Test Scores?',
    subtitle:
        'Test-optional made the question harder, not easier - the honest answer is per-school arithmetic, not a single decision',
    description:
        'The same 1480 is a submit at one school on your list and a withhold at another. This playbook decodes what test-optional actually means in practice, gives you the middle-50% rule for the submit line, and covers the fine print families miss: scholarship score requirements, major-specific expectations, and the policies that aren\'t optional at all.',
    readTime: '10 min read',
    infographic: '/resources-infographics/should-you-submit-test-scores-portrait-v1.jpg',
    infographicWide: true,
    category: 'Student Playbook',
    publishedAt: '2026-07-16',

    card: {
        icon: 'PencilSquareIcon',
        gradient: 'from-violet-600 to-purple-700',
        accentColor: '#7C3AED',
        bgPattern:
            'radial-gradient(circle at 25% 25%, rgba(124,58,237,0.10) 0%, transparent 50%), radial-gradient(circle at 80% 75%, rgba(147,51,234,0.07) 0%, transparent 50%)',
    },


    sections: [
        {
            id: 'what-optional-means',
            title: 'What "test-optional" actually means',
            body: `Test-optional means one narrow thing: **the school will render a decision without a score if you don't send one.** It does not mean scores stopped mattering. At most test-optional schools, a strong submitted score still functions exactly the way it always did - as evidence.

The policies come in real families, and the labels matter:

- **Test-required** - scores are back, or never left. No decision to make: you're testing, and the only question is when.
- **Test-optional** - the big middle. You choose per school whether your score helps.
- **Test-blind / test-free** - the school won't look at scores even if you send them. Also no decision to make - but note that a *system* being blind for admission doesn't always extend to placement or scholarships.

Two practical notes that trip families up every cycle. Policies **change** - several selective schools have reinstated requirements in recent years, and a policy you read about in a two-year-old forum post may be gone. Check the school's own page, this cycle. And "optional" sometimes has fine print: specific programs, home-schooled applicants, or certain scholarships within an otherwise optional school may still require scores.`,
            visual: { component: 'ChipCards', props: { columns: 3, items: [
                { title: 'Required', text: 'You\'re testing. The only variable is when and how many times.' },
                { title: 'Optional', text: 'A real per-school decision - the rest of this paper.', emphasis: true },
                { title: 'Blind / free', text: 'Scores unread for admission. Check placement + scholarship fine print anyway.' },
            ] } },
        },
        {
            id: 'the-submit-line',
            title: 'The submit line: middle-50% arithmetic',
            body: `Strip away the anxiety and the submit decision is arithmetic against one number pair: the school's **admitted middle 50%** - the 25th-to-75th-percentile score band of the students it enrolled.

The rule that survives contact with reality:

- **At or above the middle of their band -> submit.** Your score is evidence in your favor; withholding it just deletes a point in your column.
- **Below their 25th percentile -> withhold** (where genuinely optional). A score meaningfully below the band invites the reader to weigh a weakness you were allowed to omit.
- **In between -> judgment.** Toward the top of the band, submitting usually helps, especially when the rest of the file needs a quantitative anchor. Toward the bottom, it depends what story the rest of your application tells without it.

One more honest wrinkle: at test-optional schools, the *published* middle 50% reflects only students who submitted - which pushes the visible band higher than the true enrolled class. Treat the published band as slightly inflated and don't panic-withhold a score sitting just under the 50th percentile.`,
            visual: { component: 'StatStrip', props: { stats: [
                { value: '≥ 50th', label: 'of their admitted band', note: 'submit - it\'s evidence for you' },
                { value: '< 25th', label: 'of their admitted band', note: 'withhold where truly optional' },
                { value: 'between', label: 'judgment territory', note: 'depends on the rest of your file' },
            ], footer: 'Published bands at optional schools skew high - submitters only. Read them with a grain of salt.' } },
        },
        {
            id: 'per-school-not-global',
            title: 'The decision is per school - the same score splits your list',
            body: `Here's what makes this genuinely confusing for families: **submit-or-withhold is not one decision. It's one decision per school.** The same 1480 sits above one school's 75th percentile, squarely inside another's middle, and under a third's 25th - on the same list, in the same season. Submitting everywhere or withholding everywhere are both usually wrong.

This is exactly the kind of clerical-plus-judgment work that eats families in October: eight schools, eight score bands, eight policies with fine print, cross-referenced against one score report. It's also mechanical once the data is in one place - a table of your list's policies and bands next to your own numbers turns a family argument into a checklist.

And if you're deciding whether to **retest**: the same arithmetic answers it. A retake matters when it would move you across a line that changes decisions - from below a key school's 25th into the band, or from mid-band to above the 75th where merit money often lives. A retake that moves you thirty points within the same band changes almost nothing except your Saturday.`,
        },
        {
            id: 'the-fine-print',
            title: 'The fine print: scholarships, majors, and placement',
            body: `The admission decision is only one door your scores open or close. Three others families miss:

**Merit scholarships.** Many schools with published automatic-merit tiers key them to GPA *plus test scores* - and at some otherwise test-optional schools, the biggest merit awards still want a score. Withholding a score that would have triggered a five-figure automatic scholarship is an expensive kind of tidy.

**Majors with expectations.** Officially, optional means optional for everyone. In practice, quantitative programs - engineering, CS, business at some schools - read a strong math score as reassurance, and its absence as a question the rest of your transcript has to answer loudly.

**Placement and honors.** Scores sometimes place you out of intro courses, into honors colleges, or past prerequisites - value that exists entirely outside the admit decision.

The pattern in all three: *"do I submit for admission?"* is too narrow a question. The right question is **"where does this score help me anywhere in this school's machinery?"** - and that answer, too, is per school.`,
            visual: { component: 'ChipCards', props: { columns: 3, items: [
                { title: 'Merit money', text: 'Automatic scholarship tiers often key to scores - withholding can cost real dollars.', emphasis: true },
                { title: 'Quantitative majors', text: 'A strong math score reassures engineering/CS readers even where optional.' },
                { title: 'Placement & honors', text: 'Scores open doors after admission too - intro-course waivers, honors colleges.' },
            ] } },
        },
        {
            id: 'how-stratia-solves-this',
            title: 'How Stratia solves this',
            body: `Every fit read in Stratia carries the two facts this decision needs, per school:

- **The school's actual test policy** - required, optional, or blind, pulled from its knowledge-base profile, this cycle.
- **Its admitted band next to your numbers** - so the submit line is visible at a glance instead of argued at the dinner table.

The same read flags where a score opens scholarship or program doors - so the whole question compresses to a glance per school, which is what it should have been all along.`,
        },
        {
            id: 'what-this-gets-you',
            title: 'What this gets you',
            body: `- **One rule instead of one panic**: your score against each school's admitted band - submit above the middle, withhold below the 25th, judgment between.
- **A per-school answer sheet** - because the same score legitimately splits a list.
- **A retest decision made with arithmetic** - retake only when it crosses a line that changes something.
- **No scholarship left on the table** by a withhold that was optimizing the wrong door.

Find your fit. Build your plan. Tell your story.`,
        },
    ],
};

export default paper;
